<template>
  <div class="relative max-h-[552px] w-full max-w-[561px]">
    <div
      class="w-full rounded-[22px] border border-gray-200 bg-white text-gray-500 shadow-md dark:shadow-none"
      :class="isExpanded ? 'absolute z-10 mt-[0] ' : ''"
    >
      <div class="flex h-[44px] w-full items-center py-3 pl-[22px] sm:max-w-xl lg:max-w-2xl">
        <ClassicNtpIconsSearch class="ml-[-2px] mr-3 h-[16px] text-gray-700" />
        <input
          id="searchbox-input"
          ref="searchInput"
          v-model.trim="search"
          v-autofocus-on-document-visible
          role="searchbox"
          :aria-expanded="isExpanded"
          type="text"
          class="ml-1 mr-8 flex-grow bg-inherit text-black placeholder-gray-500 focus:outline-none"
          autocomplete="off"
          tabindex="-1"
          :placeholder="$t('search_web_url')"
          @keydown.down="cursorPosition <= suggestions.length ? ++cursorPosition : (cursorPosition = 0)"
          @keydown.up="cursorPosition === 0 ? suggestions.length : --cursorPosition"
          @keydown.enter="openSearchOnEnter()"
          @keydown.esc="cleanSearch()"
          @focusin="isSearchInputFocused = true"
          @focusout="!isSearchResultsFocused ? (isSearchInputFocused = false) : null"
        />
      </div>

      <div
        v-if="isExpanded"
        class="search-suggestions-list z-20 w-full items-center"
        @mouseenter="isSearchResultsFocused = true"
        @mouseleave="isSearchResultsFocused = false"
      >
        <ul class="m-0 mb-3 p-0 text-black">
          <li
            class="flex h-[42px] w-full cursor-pointer flex-row items-center gap-3 pl-[18px] hover:bg-gray-100"
            :class="cursorPosition === 0 ? 'bg-gray-100' : ''"
            @click="openSearchOnClick(search)"
          >
            <ClassicNtpIconsSearch class="h-[14px] text-gray-500" />
            <div>{{ search }} <span class="text-gray-500"> - Web Search</span></div>
          </li>
          <li
            v-for="(suggestion, index) in suggestions"
            :key="suggestion?.text + suggestion.defaultImgProp"
            :class="cursorPosition - 1 === index ? 'bg-gray-100' : ''"
            class="flex h-[42px] w-full cursor-pointer flex-row items-center gap-3 pl-[18px] hover:bg-gray-100"
            @click="openSearchOnClick(suggestion.text)"
          >
            <ClassicNtpIconsSearch class="h-[14px] text-gray-500" />
            <div>{{ suggestion?.text }}</div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { watchDebounced } from '@vueuse/core'
  import { getSuggestions, type SearchSuggestion } from 'assets/js/searchsuggestions'
  import useHostBrowserConfig from '~/composables/onelaunch/useHostBrowserConfig.client'
  import useActiveSearchProvider from '~/composables/shift/useActiveSearchProvider'
  import * as HostBrowserApis from '~/utils/host-browser-apis'

  interface PredictiveSearchboxProps {
    searchEngineResultsPage?: string
    searchParam?: string
  }

  const props = withDefaults(defineProps<PredictiveSearchboxProps>(), {
    searchEngineResultsPage: 'https://search.yahoo.com/yhs/search?p={searchTerms}',
    searchParam: '{searchTerms}',
  })

  const hostBrowserConfig = useHostBrowserConfig()

  const searchInput = ref<HTMLInputElement | null>(null)
  const search = ref('')
  const isSearchInputFocused = ref(false)
  const isSearchResultsFocused = ref(false)
  const suggestions = ref<SearchSuggestion[]>([])
  const cursorPosition = ref(0)

  const isExpanded = computed(() => isSearchInputFocused.value && suggestions.value?.length > 0)

  watchDebounced(
    search,
    async (value) => {
      if (value.length === 0) {
        suggestions.value = []
        return
      }

      if (value.length > 2) {
        const suggestionList = await getPredictiveSearchResults(value)
        suggestions.value = Object.values(suggestionList)
      }
    },
    { debounce: 90 }
  )

  function cleanSearch() {
    isSearchInputFocused.value = true
    suggestions.value = []
    search.value = ''
    isSearchResultsFocused.value = false
    cursorPosition.value = 0
  }

  async function openSearchOnEnter() {
    if (!search.value) return
    const selectedSuggestion = suggestions.value[cursorPosition.value - 1]?.text || search.value
    const { url, isMonetized } = await buildLinkForQuery(selectedSuggestion)
    await logQueryEvent('keyboard-return', { isMonetized })

    window.open(url, '_blank')
    cleanSearch()
  }

  async function openSearchOnClick(query: string) {
    const { url, isMonetized } = await buildLinkForQuery(query)
    await logQueryEvent('suggestion-press', { isMonetized })

    window.open(url, '_blank')
    cleanSearch()
    searchInput?.value?.focus()
  }

  async function logQueryEvent(
    trigger: 'suggestion-press' | 'keyboard-return',
    { isMonetized }: { isMonetized: boolean | undefined }
  ) {
    const isMonetizationApiAvailable = isMonetized !== undefined
    if (isMonetizationApiAvailable) {
      const activeSearchProvider = await useActiveSearchProvider()
      if (isMonetized) {
        return HostBrowserApis.sendMixpanelEvent('query', {
          location: 'ntp',
          monetized: true,
          trigger,
          active_search_provider: activeSearchProvider,
        })
      }

      if (!isMonetized) {
        return HostBrowserApis.sendMixpanelEvent('QueryNonMonetized_Submitted', {
          location: 'ntp',
          monetized: false,
          trigger,
          active_search_provider: activeSearchProvider,
        })
      }
    }

    if (!isMonetizationApiAvailable) {
      await HostBrowserApis.sendMixpanelEvent('query', { location: 'ntp', trigger })
    }
  }

  async function getPredictiveSearchResults(searchTerm: string): Promise<Record<number, SearchSuggestion>> {
    if (hostBrowserConfig.value) {
      const { rich_suggest_url, suggest_url } = hostBrowserConfig.value

      if (rich_suggest_url) {
        const data = await getSuggestions(searchTerm, rich_suggest_url)
        return data ?? []
      }

      if (suggest_url) {
        const data = await getSuggestions(searchTerm, suggest_url)
        return data ?? []
      }
    }

    const data = await getSuggestions(searchTerm, null)
    return data ?? []
  }

  async function buildLinkForQuery(query: string) {
    return HostBrowserApis.getSearchUrl(query, {
      fallbackUrl: props.searchEngineResultsPage,
      queryPlaceholder: props.searchParam,
    })
  }
</script>
